type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export type PersistedFlagStoreOptions = {
  storageKey: string;
  storage?: StorageLike | null;
};

const getDefaultStorage = (): StorageLike | null => {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
};

const parseFlagIds = (raw: string | null): Set<string> => {
  if (!raw) return new Set();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
};

/** Persists a set of string flag ids in localStorage (or any Storage-like backend). */
export function createPersistedFlagStore({
  storageKey,
  storage = getDefaultStorage(),
}: PersistedFlagStoreOptions) {
  const read = (): Set<string> => {
    if (!storage) return new Set();
    return parseFlagIds(storage.getItem(storageKey));
  };

  const write = (flags: Set<string>): void => {
    if (!storage) return;
    if (flags.size === 0) {
      storage.removeItem(storageKey);
      return;
    }
    storage.setItem(storageKey, JSON.stringify([...flags]));
  };

  return {
    has(flagId: string): boolean {
      return read().has(flagId);
    },
    set(flagId: string): void {
      const flags = read();
      if (flags.has(flagId)) return;
      flags.add(flagId);
      write(flags);
    },
    clear(flagId: string): void {
      const flags = read();
      if (!flags.delete(flagId)) return;
      write(flags);
    },
    clearAll(): void {
      storage?.removeItem(storageKey);
    },
  };
}

/** Builds a stable, namespaced flag id (e.g. per user on a shared browser). */
export const scopedFlagId = <TScope extends string, TFlag extends string>(
  scope: TScope,
  flagId: TFlag,
): `${TScope}:${TFlag}` => `${scope}:${flagId}`;
